import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, AlertTriangle, Tag } from 'lucide-react';
import './CompleteAssignmentModal.css';

const ROLE_LABEL = {
  'classifier':              'Classifier',
  'impact-signer-technical': 'Technical impact signer',
  'impact-signer-structural':'Structural impact signer',
  'impact-signer-compliance':'Compliance impact signer',
  'impact-signer-baseline':  'Baseline impact signer',
  'author':                  'Author',
  'technical-reviewer':      'Technical reviewer',
  'independent-verifier':    'Independent verifier',
  'configuration-updater':   'Configuration updater',
  'releaser':                'Release authoriser',
  'closer':                  'Closer',
};

/**
 * Compute valid outcomes per assignment role. Mirrors the backend's
 * VALID_OUTCOMES_BY_ROLE table — keep these in sync.
 */
function outcomesForRole(role) {
  if (role === 'classifier') {
    return [{ value: 'accept', label: 'Accept', tone: 'accept' }];
  }
  if (role && role.startsWith('impact-signer-')) {
    return [
      { value: 'accept',               label: 'Accept',                 tone: 'accept' },
      { value: 'accept-with-comments', label: 'Accept with comments',   tone: 'accept' },
      { value: 'escalate',             label: 'Escalate classification', tone: 'warn' },
    ];
  }
  if (role === 'author') {
    return [{ value: 'accept', label: 'Submit for review', tone: 'accept' }];
  }
  if (role === 'technical-reviewer') {
    return [
      { value: 'accept',               label: 'Accept',                 tone: 'accept' },
      { value: 'accept-with-comments', label: 'Accept with comments',   tone: 'accept' },
      { value: 'reject',               label: 'Reject (hard stop)',     tone: 'reject' },
    ];
  }
  if (role === 'independent-verifier') {
    return [
      { value: 'accept',               label: 'Accept',                 tone: 'accept' },
      { value: 'accept-with-comments', label: 'Accept with comments',   tone: 'accept' },
      { value: 'reject',               label: 'Reject (hard stop)',     tone: 'reject' },
    ];
  }
  if (role === 'configuration-updater') {
    return [{ value: 'accept', label: 'Confirm configuration update', tone: 'accept' }];
  }
  if (role === 'releaser') {
    return [
      { value: 'authorise', label: 'Authorise release', tone: 'accept' },
      { value: 'hold',      label: 'Hold',              tone: 'warn'   },
      { value: 'reject',    label: 'Reject release',    tone: 'reject' },
    ];
  }
  if (role === 'closer') {
    return [{ value: 'accept', label: 'Close', tone: 'accept' }];
  }
  return [];
}

// Role-specific placeholder text for the impact-signer notes field.
// Steers each signer toward the right kind of findings.
function impactNotesPlaceholder(role) {
  switch (role) {
    case 'impact-signer-technical':
      return 'Cross-system effects, performance, manufacturing, applicable technical standards. E.g. "Affects RCS budget via skin tolerance; no new DTs needed if author coordinates with antennas team."';
    case 'impact-signer-structural':
      return 'Load paths, dynamic behaviour, fatigue/DT implications, methods/codes. E.g. "Thickness shifts fatigue-critical regions — recommend adding subject #7 to scope."';
    case 'impact-signer-compliance':
      return 'USAR/AEP-4671 paragraphs, special conditions, certification basis. E.g. "USAR §603, §613, §305 apply; no new SC if material is in Annex C-1 qualified list."';
    case 'impact-signer-baseline':
      return 'Affected configuration baseline(s), released data invalidated, new CIs expected. E.g. "Allocated baseline (structural); ~3 new CIs expected; no released data invalidated."';
    default:
      return 'Impact findings for the author to know upfront';
  }
}

function CompleteAssignmentModal({ assignment, onClose, onCompleted, projectId: propProjectId, dtId: propDtId }) {
  const options = outcomesForRole(assignment.role);
  const [outcome, setOutcome] = useState(options[0]?.value || '');
  const [outcomeReason, setOutcomeReason] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [cveEngagementRef, setCveEngagementRef] = useState('');
  const [reviewerJustification, setReviewerJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // ── Classifier-only: subjects picker ─────────────────────────────────
  // Cristian (or whoever holds the classifier role) declares which Annex A
  // subjects this DT touches. The State 3 impact assessment spawns
  // assignments based on this list.
  const isClassifier = assignment.role === 'classifier';
  const [allSubjects, setAllSubjects] = useState([]);
  const [pickedSubjectIds, setPickedSubjectIds] = useState(
    (assignment.designTask?.affectedSubjects || []).map(s => String(s._id || s))
  );
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  useEffect(() => {
    if (!isClassifier) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingSubjects(true);
        const res = await axios.get('/subjects');
        if (cancelled) return;
        setAllSubjects((res.data || []).sort((a, b) => a.number - b.number));
      } catch (err) {
        if (!cancelled) console.error('Failed to load subjects:', err);
      } finally {
        if (!cancelled) setLoadingSubjects(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isClassifier]);

  const toggleSubject = (id) => {
    setPickedSubjectIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const isImpactSigner = assignment.role && assignment.role.startsWith('impact-signer-');
  const isReviewer = assignment.role === 'technical-reviewer';
  const isVerifier = assignment.role === 'independent-verifier';
  const isReleaser = assignment.role === 'releaser';
  const isCloser   = assignment.role === 'closer';

  const needsEscalationReason = isImpactSigner && outcome === 'escalate';
  const needsOutcomeReason = (isReviewer && outcome === 'reject') ||
                             (isVerifier && outcome === 'reject') ||
                             (isReleaser && ['hold', 'reject'].includes(outcome));
  const needsAttachmentUrl = isVerifier && outcome === 'accept';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!outcome) { setError('Select an outcome to continue.'); return; }
    if (isClassifier && pickedSubjectIds.length === 0) {
      setError('Pick at least one affected subject — the impact assessment needs to know what this DT touches.');
      return;
    }
    if (needsEscalationReason && !escalationReason.trim()) {
      setError('Escalation requires a reason.'); return;
    }
    if (needsOutcomeReason && !outcomeReason.trim()) {
      setError('A reason is required for this outcome.'); return;
    }
    if (needsAttachmentUrl && !attachmentUrl.trim()) {
      setError('Verification accept requires a verification record attachment URL.');
      return;
    }

    setSubmitting(true);
    try {
      // Classifier path: persist the affectedSubjects on the DT BEFORE
      // completing the assignment, so the auto-advance to State 3 has the
      // data it needs to spawn the right impact-signer assignments.
      if (isClassifier) {
        const dtId = propDtId
                  || assignment.designTask?._id
                  || assignment.designTask;
        const projectId = propProjectId
                       || assignment.designTask?.project?._id
                       || assignment.designTask?.project
                       || assignment.project;
        if (!dtId || !projectId) {
          throw new Error('Cannot resolve DT or project id from assignment.');
        }
        await axios.put(`/projects/${projectId}/designtasks/${dtId}`, {
          affectedSubjects: pickedSubjectIds,
        });
      }

      const body = { outcome };
      if (outcomeReason)         body.outcomeReason = outcomeReason;
      if (escalationReason)      body.outcomeReason = escalationReason;  // sent as outcomeReason; backend reads it for escalate
      if (attachmentUrl)         body.attachmentUrl = attachmentUrl;
      if (cveEngagementRef)      body.cveEngagementRef = cveEngagementRef;
      if (reviewerJustification) body.reviewerJustification = reviewerJustification;
      if (needsEscalationReason) body.escalationReason = escalationReason;

      const res = await axios.put(`/assignments/${assignment._id}/complete`, body);
      onCompleted(res.data);
    } catch (err) {
      setError(err.response?.data?.error
            || err.response?.data?.errors?.[0]?.msg
            || err.message
            || 'Failed to complete assignment.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dt-modal-backdrop" onClick={onClose}>
      <div className="dt-modal cam-modal" onClick={e => e.stopPropagation()}>
        <header className="dt-modal-header">
          <div>
            <p className="dt-modal-eyebrow">Complete assignment</p>
            <h2>{ROLE_LABEL[assignment.role] || assignment.role}</h2>
            {assignment.relatedSubject && (
              <p className="cam-subject">
                Subject {assignment.relatedSubject.number} — {assignment.relatedSubject.name}
              </p>
            )}
          </div>
          <button className="dt-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="dt-wizard-form cam-form">

          {/* OUTCOME PICKER */}
          <fieldset className="dt-fieldset">
            <legend>Outcome *</legend>
            <div className="cam-outcomes">
              {options.map(opt => (
                <label
                  key={opt.value}
                  className={`cam-outcome cam-outcome-${opt.tone} ${outcome === opt.value ? 'is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="outcome"
                    value={opt.value}
                    checked={outcome === opt.value}
                    onChange={() => setOutcome(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* CLASSIFIER: subjects picker — required to drive the State 3 impact assessment */}
          {isClassifier && (
            <fieldset className="dt-fieldset">
              <legend>
                <Tag size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
                Affected Annex A subjects *
              </legend>
              <p className="cam-subjects-hint">
                Pick every subject this DT touches. Impact-signer assignments at State 3
                will be created based on these selections.
              </p>
              {loadingSubjects ? (
                <div className="loading-spinner" style={{ width: 18, height: 18, margin: '8px 0' }}></div>
              ) : (
                <div className="cam-subj-grid">
                  {allSubjects.map(s => {
                    const checked = pickedSubjectIds.includes(String(s._id));
                    return (
                      <label
                        key={s._id}
                        className={`cam-subj-chip cam-subj-${s.domain} ${checked ? 'is-picked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSubject(String(s._id))}
                        />
                        <span className="cam-subj-num">#{s.number}</span>
                        <span className="cam-subj-name">{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="cam-subjects-count">
                {pickedSubjectIds.length} of {allSubjects.length} selected
              </p>
            </fieldset>
          )}

          {/* HARD-STOP WARNING */}
          {(isReviewer || isVerifier) && outcome === 'reject' && (
            <div className="cam-warn">
              <AlertTriangle size={14} />
              <span>Reject is a hard stop. The Design Task returns to State 4 (Drafting) and the open peer assignments will be closed as "skipped".</span>
            </div>
          )}

          {isImpactSigner && outcome === 'escalate' && (
            <div className="cam-warn">
              <AlertTriangle size={14} />
              <span>Escalation returns the Design Task to State 2 (Classified) for re-classification by Config &amp; Compliance.</span>
            </div>
          )}

          {isReleaser && outcome === 'reject' && (
            <div className="cam-warn">
              <AlertTriangle size={14} />
              <span>Reject at release cancels the Design Task (State 11). The decision is permanent.</span>
            </div>
          )}

          {/* CONDITIONAL FIELDS */}
          {needsEscalationReason && (
            <div className="dt-field">
              <label htmlFor="cam-esc">Escalation reason *</label>
              <textarea
                id="cam-esc"
                rows={3}
                value={escalationReason}
                onChange={e => setEscalationReason(e.target.value)}
                placeholder="Why the proposed classification underestimates the impact"
              />
            </div>
          )}

          {needsOutcomeReason && (
            <div className="dt-field">
              <label htmlFor="cam-reason">Reason *</label>
              <textarea
                id="cam-reason"
                rows={3}
                value={outcomeReason}
                onChange={e => setOutcomeReason(e.target.value)}
                placeholder="Findings or rationale (will appear in the audit log)"
              />
            </div>
          )}

          {needsAttachmentUrl && (
            <div className="dt-field">
              <label htmlFor="cam-attach">Verification record URL *</label>
              <input
                id="cam-attach"
                type="text"
                value={attachmentUrl}
                onChange={e => setAttachmentUrl(e.target.value)}
                placeholder="/uploads/KDS-VR-2026-0042.pdf or https://…"
              />
              <span className="dt-field-hint">
                Signed verification record per P-04. Required for independent verifier accept.
              </span>
            </div>
          )}

          {/* Optional notes field — for roles that produce a narrative
              alongside their accept (impact signers, closer). Captured
              as outcomeReason on the assignment so it surfaces in the
              assignment-details block on the DT detail page. */}
          {isImpactSigner && outcome === 'accept' && (
            <div className="dt-field">
              <label htmlFor="cam-impact-notes">Impact notes</label>
              <textarea
                id="cam-impact-notes"
                rows={4}
                value={outcomeReason}
                onChange={e => setOutcomeReason(e.target.value)}
                placeholder={impactNotesPlaceholder(assignment.role)}
              />
              <span className="dt-field-hint">
                What in your domain does this DT affect? Findings and conditions for the author
                to know upfront. Appears in the audit trail and on the DT detail page.
              </span>
            </div>
          )}

          {isCloser && outcome === 'accept' && (
            <div className="dt-field">
              <label htmlFor="cam-closure">Closure summary</label>
              <textarea
                id="cam-closure"
                rows={3}
                value={outcomeReason}
                onChange={e => setOutcomeReason(e.target.value)}
                placeholder="What was delivered, where it lives, any follow-on items"
              />
              <span className="dt-field-hint">
                Short summary captured in the State Transition Log and the audit-export CSV.
              </span>
            </div>
          )}

          {isVerifier && (
            <div className="dt-field">
              <label htmlFor="cam-cve">CVE engagement ref (if applicable)</label>
              <input
                id="cam-cve"
                type="text"
                value={cveEngagementRef}
                onChange={e => setCveEngagementRef(e.target.value)}
                placeholder="e.g. KDS-CVE-A-2026-0042"
              />
            </div>
          )}

          {isReviewer && (
            <div className="dt-field">
              <label htmlFor="cam-just">Reviewer justification (optional)</label>
              <textarea
                id="cam-just"
                rows={2}
                value={reviewerJustification}
                onChange={e => setReviewerJustification(e.target.value)}
                placeholder="Required if you're not Lead-competent in this subject"
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="dt-modal-actions">
            <button type="button" className="dt-btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="dt-btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit outcome'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompleteAssignmentModal;
